import React, { useEffect, useState } from 'react';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Eye, EyeOff, RotateCcw, LayoutList } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { MODS } from './dashUtils';

export const DEFAULT_ORDER = ['summary', 'sales', 'ecommerce', 'pos', 'inventory', 'finance', 'purchasing', 'crm', 'email_marketing', 'pillot_pay', 'pesan', 'hr'];
const key = (cid, uid) => `pillot.dash.layout.${cid}.${uid}`;

/** Per-user section order + hidden set, persisted in localStorage (per company). */
export const useDashLayout = (companyId, userId) => {
  const [layout, setLayout] = useState({ order: DEFAULT_ORDER, hidden: [] });
  useEffect(() => {
    if (!companyId || !userId) return;
    try {
      const raw = JSON.parse(localStorage.getItem(key(companyId, userId)) || 'null');
      if (raw?.order) setLayout({ order: [...raw.order.filter((k) => DEFAULT_ORDER.includes(k)), ...DEFAULT_ORDER.filter((k) => !raw.order.includes(k))], hidden: (raw.hidden || []).filter((k) => DEFAULT_ORDER.includes(k)) });
    } catch { /* ignore */ }
  }, [companyId, userId]);
  const save = (next) => { setLayout(next); if (companyId && userId) localStorage.setItem(key(companyId, userId), JSON.stringify(next)); };
  return [layout, save];
};

const Row = ({ id, hidden, onToggle, lang }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const m = MODS[id];
  return (
    <li ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className={`flex items-center gap-2 rounded-xl border border-border bg-white px-2 py-2 ${isDragging ? 'z-10 shadow-lg' : ''} ${hidden ? 'opacity-60' : ''}`} data-testid={`dash-layout-row-${id}`}>
      <button type="button" className="cursor-grab touch-none rounded-md p-1 text-muted-foreground hover:bg-muted active:cursor-grabbing" {...attributes} {...listeners} aria-label="drag" data-testid={`dash-layout-grip-${id}`}><GripVertical size={16} /></button>
      <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: m.color }} />
      <span className="flex-1 text-sm font-medium">{m.label[lang] || m.label.id}</span>
      {hidden ? <EyeOff size={14} className="text-muted-foreground" /> : <Eye size={14} className="text-muted-foreground" />}
      <Switch checked={!hidden} onCheckedChange={onToggle} data-testid={`dash-layout-toggle-${id}`} />
    </li>
  );
};

export const LayoutDialog = ({ layout, onSave, onClose, lang = 'id' }) => {
  const [order, setOrder] = useState(layout.order);
  const [hidden, setHidden] = useState(layout.hidden);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const id = lang === 'id';
  const onDragEnd = ({ active, over }) => { if (over && active.id !== over.id) setOrder((o) => arrayMove(o, o.indexOf(active.id), o.indexOf(over.id))); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-[min(520px,calc(100vw-24px))] max-w-none overflow-y-auto rounded-2xl" data-testid="dash-layout-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><LayoutList size={18} />{id ? 'Atur tampilan dashboard' : 'Arrange dashboard'}</DialogTitle><DialogDescription>{id ? 'Seret untuk mengubah urutan, matikan untuk menyembunyikan section. Tersimpan untuk akun Anda di perangkat ini.' : 'Drag to reorder, switch off to hide a section. Saved for your account on this device.'}</DialogDescription></DialogHeader>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            <ul className="space-y-1.5">{order.map((k) => <Row key={k} id={k} hidden={hidden.includes(k)} lang={lang} onToggle={(v) => setHidden((h) => (v ? h.filter((x) => x !== k) : [...h, k]))} />)}</ul>
          </SortableContext>
        </DndContext>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" className="rounded-xl" onClick={() => { setOrder(DEFAULT_ORDER); setHidden([]); }} data-testid="dash-layout-reset"><RotateCcw size={14} className="mr-1" />{id ? 'Reset' : 'Reset'}</Button>
          <div className="flex gap-2"><Button variant="outline" className="rounded-xl" onClick={onClose}>{id ? 'Batal' : 'Cancel'}</Button><Button className="rounded-xl" onClick={() => { onSave({ order, hidden }); onClose(); }} data-testid="dash-layout-save">{id ? 'Simpan' : 'Save'}</Button></div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
